import { AbsenceListDto, AbsenceType } from '@lmt/shared-types';
import {
  DXRS,
  MonthViewBase,
} from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-react-scheduler';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { ReactComponent as HolidayIcon } from '@lmt/unicorn-shared/app/icons/holiday.svg';
import clsx from 'clsx';
import { addDays, addHours, isWithinInterval } from 'date-fns';
import React, { MouseEventHandler, useCallback, useMemo } from 'react';
import styles from './Calendar.module.scss';
import { CalendarDateCellDropHandler } from './DragAndDropPatient';
import { useEventCallback } from '@mui/material';

const defaultStartDayHour = 9;

export type Props = {
  defaultTypeId?: number | null;
  showAbsences?: boolean;
  absences?: AbsenceListDto[];
  isHolidayView?: boolean;
  onHolidayMouseEnter?: (
    target: HTMLElement,
    absence: AbsenceListDto[] | undefined,
    date: Date,
  ) => void;
  handlePopupClose?: () => void;
  onMouseOver?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
};

export const MonthViewTimeTableCell: React.FC<
  MonthViewBase.TimeTableCellProps & Props
> = React.memo(function MonthViewTimeTableCell(props) {
  const {
    defaultTypeId,
    showAbsences,
    absences,
    isHolidayView,
    onHolidayMouseEnter,
    handlePopupClose,
    ...rest
  } = props;
  const forms = useGlobalForms();
  const claims = useClaims();
  const dayOfMonth = props.startDate.getDate();
  const holidaysForDay: AbsenceListDto[] | undefined = useMemo(() => {
    if (!props.showAbsences) {
      return undefined;
    }
    return props.absences
      ?.filter(
        (x) =>
          x.absenceType === AbsenceType.Holiday &&
          isWithinInterval(props.startDate, {
            start: x.start,
            end: x.end,
          }) &&
          props.startDate.getTime() !== x.end.getTime(),
      )
      .sort((a, b) =>
        a.title.toLowerCase() > b.title.toLowerCase()
          ? 1
          : b.title.toLowerCase() > a.title.toLowerCase()
            ? -1
            : 0,
      );
  }, [props.showAbsences, props.absences]);
  const holiday: AbsenceListDto | undefined = holidaysForDay
    ? holidaysForDay[0]
    : undefined;

  const isFirstDayOfHoliday = useMemo(() => {
    if (!holiday) {
      return false;
    }
    return props.startDate.getTime() === holiday.start.getTime();
  }, [holiday]);

  const classNames = clsx(styles.monthDayNumber, {
    [styles.otherMonthDay]: props.otherMonth,
    [styles.monthTodayCell]: props.today,
    [styles.holidayTitle]: props.showAbsences && !!holiday,
  });

  const cellClassNames = clsx(styles.monthTimeTableCell, {
    [styles.holidayTimeTableCell]: props.showAbsences && !!holiday,
  });

  const doubleClickHandler = useEventCallback(() => {
    if (props.isHolidayView) {
      if (!claims.permissions.manageAbsences) {
        return;
      }
      props.handlePopupClose?.();
      if (holiday) {
        forms.openAbsenceEditing(holiday.id);
        return;
      }
      forms.openAbsenceCreating({
        start: props.startDate,
        end: props.endDate ? props.endDate : addDays(props.startDate, 1),
      });
      return;
    }
    const startDate = addHours(props.startDate, defaultStartDayHour); // 9:00 AM
    const interval = {
      start: startDate,
      end: addHours(startDate, 1),
    };
    forms.openVisitCreating(
      interval,
      undefined,
      undefined,
      undefined,
      props.defaultTypeId,
    );
  });

  // WeekView.TimeTableCell is used intentionally instead of MonthView.TimeTableCell
  // as it doesn't have children prop and so doesn't allow passing CalendarDateCellDropHandler
  return (
    <>
      <DXRS.WeekView.TimeTableCell
        {...rest}
        className={cellClassNames}
        onDoubleClick={doubleClickHandler}
        onMouseEnter={(e: { target: HTMLElement }) => {
          props.onHolidayMouseEnter?.(
            e.target,
            holiday ? holidaysForDay : undefined,
            props.startDate,
          );
        }}
      >
        <div className={classNames}>{dayOfMonth}</div>
        {isFirstDayOfHoliday && (
          <div
            className={clsx(styles.holidayCellHeaderBase, {
              [styles.cellHeaderSingleDigitDay]: dayOfMonth < 9,
              [styles.cellHeaderToday]: props.today,
            })}
          >
            <HolidayIcon className={styles.holidayIcon} />
            <span>{holiday!.title}</span>
          </div>
        )}
        <CalendarDateCellDropHandler
          startDate={addHours(props.startDate, defaultStartDayHour)}
          endDate={addHours(props.startDate, defaultStartDayHour + 1)}
        />
      </DXRS.WeekView.TimeTableCell>
    </>
  );
});
